import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Text } from '@src/design-system/text';
import { Input } from '@src/design-system/input';
import fileCheck from '@shared/assets/svgs/file-check.svg';
import { Edit2, Trash } from 'iconsax-react';

interface FilePreviewProps {
  file: File;
  onDelete: () => void;
  onRename: (newName: string) => void;
  showDelete?: boolean;
  autoEdit?: boolean; // ✅ new prop to control edit mode
}

export default function FilePreview({
  file,
  onDelete,
  onRename,
  showDelete = true,
  autoEdit = false,
}: FilePreviewProps) {
  const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
  const fileExtension = file.name.split('.').pop();
  const [fileName, setFileName] = useState(fileNameWithoutExt);
  const [isEditing, setIsEditing] = useState(autoEdit);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    onRename(`${fileName}.${fileExtension}`);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <div className='flex items-center justify-between border rounded-lg p-3 bg-primary-30 w-full border-primary-200'>
      <div className='flex items-center gap-3'>
        <div>
          <img
            src={fileCheck}
            className='w-10 h-10'
            alt='file check'
          />
        </div>

        {isEditing ? (
          <div className='flex items-center gap-2'>
            <Input
              ref={inputRef}
              type='text'
              value={fileName}
              onChange={handleFileNameChange}
              variant='outline'
              className='border-none focus:border-none focus-within:border-none focus-within:ring-0 selection:bg-gray-100 selection:text-primary-800 p-0 w-auto'
              style={{ width: `${fileName.length + 1}ch` }}
            />
            <Check
              className='text-primary-600 cursor-pointer hover:text-primary-800'
              size={16}
              onClick={handleBlur}
            />
          </div>
        ) : (
          <div>
            <div className='flex items-center gap-2'>
              <Text className='text-gray-800 text-sm font-medium'>
                {fileName}.{fileExtension}
              </Text>
              <Edit2
                onClick={handleEditClick}
                size='16'
                className='text-gray-700 cursor-pointer hover:text-gray-700'
                variant='TwoTone'
              />
            </div>
            <Text className='text-gray-600 text-sm font-normal'>
              {(file.size / 1024 / 1024).toFixed(4)} MB
            </Text>
          </div>
        )}
      </div>

      {showDelete && (
        <Trash
          size='20'
          onClick={onDelete}
          className='text-red-500 cursor-pointer hover:text-red-700'
          variant='TwoTone'
        />
      )}
    </div>
  );
}
